/**
 * useDeferredSend — E-93 Story 93.10.
 *
 * Gmail-style deferred-send hook. Instead of firing the POST immediately,
 * we schedule it on a timer and let the user hit "Undo" before it runs.
 * Frontend-only: the backend never sees a cancelled send because the
 * request never goes out.
 *
 * Contract:
 *
 *   - `scheduleSend(fn, windowMs)` — starts the timer. If a previous
 *     timer is still pending, it is cancelled (multi-click guard).
 *   - `cancelSend()` — aborts the pending timer; `fn` is NOT called.
 *   - `isPending` — true while a timer is armed.
 *   - `remainingSeconds` — counts down 5 → 4 → 3 → 2 → 1 → 0. Zero
 *     means the send has either fired or been cancelled.
 *
 * Cleanup on unmount is handled automatically — if the component that
 * owns this hook unmounts mid-countdown, the pending send is dropped.
 * That matches the user's intuition: leaving the page cancels the send.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

export interface UseDeferredSendResult {
  scheduleSend: (fn: () => void | Promise<void>, windowMs: number) => void;
  cancelSend: () => void;
  isPending: boolean;
  remainingSeconds: number;
}

export function useDeferredSend(): UseDeferredSendResult {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [isPending, setIsPending] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(0);

  const clearAll = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (tickRef.current !== null) {
      clearInterval(tickRef.current);
      tickRef.current = null;
    }
  }, []);

  const cancelSend = useCallback(() => {
    clearAll();
    setIsPending(false);
    setRemainingSeconds(0);
  }, [clearAll]);

  const scheduleSend = useCallback(
    (fn: () => void | Promise<void>, windowMs: number) => {
      // Cancel any previous pending send — double-click on the Send
      // button must restart the window, not stack two sends.
      clearAll();
      setIsPending(true);
      setRemainingSeconds(Math.ceil(windowMs / 1000));

      tickRef.current = setInterval(() => {
        setRemainingSeconds(prev => (prev > 0 ? prev - 1 : 0));
      }, 1000);

      timerRef.current = setTimeout(() => {
        clearAll();
        setIsPending(false);
        setRemainingSeconds(0);
        // Intentionally not awaiting — the caller owns the async story
        // (its own error handling / state). We only care that `fn` gets
        // invoked exactly once per scheduled window.
        void fn();
      }, windowMs);
    },
    [clearAll]
  );

  // Unmount cleanup. Mid-countdown unmounts drop the pending send.
  useEffect(() => {
    return () => {
      clearAll();
    };
  }, [clearAll]);

  return { scheduleSend, cancelSend, isPending, remainingSeconds };
}
